import type { ReactElement } from 'react';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import type { ColorParams } from '@bynder-studio/render-core';
import { equals } from 'rambda';
import { Divider, Dropdown } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconPlaylistAdd } from '@bynder/icons';
import { BrandColor, BrandColorPalette } from 'packages/store/brandColors/types';
import { BrandColorOnElement } from 'packages/pages/editor/RightSideMenu/FormComponents/EditorColor/utils';
import debounce from 'packages/helpers/debounce';
import generateTestId from 'packages/helpers/testIdHelpers';
import { ColorItem } from './ColorItem';

type ColorSearchProps = {
    palettes: BrandColorPalette[];
    selectedBrandColors: BrandColor[] | [];
    selectedCustomColors?: ColorParams[] | [];
    setBrandColor: (color: BrandColor) => void;
    testId: string;
    elementColors?: ColorParams[] | [];
    onAppliedClick?: (color: BrandColorOnElement | ColorParams) => void;
    openElementStylesModal?: () => void;
    configureColorsButton?: ReactElement | null;
    colorType?: 'color' | 'strokeColor';
};

const MIN_CHARACTERS_SEARCH = 1;

const ColorSearch = ({
    palettes,
    selectedBrandColors,
    selectedCustomColors = [],
    setBrandColor,
    elementColors = [],
    onAppliedClick = () => {},
    testId,
    openElementStylesModal,
    configureColorsButton = null,
    colorType = 'color',
}: ColorSearchProps) => {
    const { translate } = useTranslate();
    const [searchInputValue, setSearchInputValue] = useState('');
    const [delayedSearchValue, setDelayedSearchValue] = useState('');

    const idToBrandColor: { [brandColorId: number]: BrandColor } = useMemo(() => {
        const byId: { [brandColorId: number]: BrandColor } = {};
        palettes.forEach((palette) => {
            palette.brandColors.forEach((color) => {
                byId[color.id] = color;
            });
        });

        return byId;
    }, [palettes]);

    const onSearchWithDelay = debounce((keyword: string) => {
        if (!keyword.length || keyword.length <= MIN_CHARACTERS_SEARCH) {
            setDelayedSearchValue('');
        } else {
            setDelayedSearchValue(keyword);
        }
    }, 1000);

    const filterPalettes = useMemo(
        () =>
            delayedSearchValue.length > MIN_CHARACTERS_SEARCH
                ? palettes.reduce((acc, palette) => {
                      if (!palette.brandColors.length) {
                          return acc;
                      }

                      if (palette.name.toLowerCase().includes(delayedSearchValue.toLowerCase())) {
                          acc.push(palette);

                          return acc;
                      }

                      const paletteFilteredBrandColors = palette.brandColors.filter((color) =>
                          color.name.toLowerCase().includes(delayedSearchValue.toLowerCase()),
                      );

                      if (paletteFilteredBrandColors.length) {
                          acc.push({ ...palette, brandColors: paletteFilteredBrandColors });

                          return acc;
                      }

                      return acc;
                  }, [] as BrandColorPalette[])
                : palettes,
        [delayedSearchValue, palettes],
    );

    const isChecked = useCallback<(item: BrandColor | BrandColorOnElement | ColorParams) => boolean>(
        (item) => {
            const id = (item as BrandColor).id || (item as ColorParams).brandColorId;

            if (id) {
                // @ts-ignore
                return selectedBrandColors.some((color) => color.id === id);
            }

            return selectedCustomColors.some((color) => equals(color, item));
        },
        [selectedBrandColors, selectedCustomColors],
    );

    const testIdProp = useMemo(() => generateTestId(`${testId}_choose_from_palette_search`), [testId]);

    return (
        <>
            <Dropdown.SearchInput
                value={searchInputValue}
                onChange={(value) => {
                    setSearchInputValue(value);
                    onSearchWithDelay(value);
                }}
                placeholder={translate('editor.sidebar.shots.color.search')}
                aria-label={translate('editor.sidebar.shots.color.search.label')}
                {...testIdProp}
            />
            {!!elementColors.length && searchInputValue === '' && (
                <>
                    <Dropdown.SectionTitle>
                        {translate('editor.sidebar.shots.color.search.element.styles')}
                    </Dropdown.SectionTitle>
                    {[...elementColors]
                        .sort((a, b) => {
                            const brandColorIdSort = (b?.brandColorId ?? 0) - (a?.brandColorId ?? 0);

                            if (brandColorIdSort !== 0) {
                                return brandColorIdSort;
                            }

                            return (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0);
                        })
                        .map((color, i) => (
                            <ColorItem
                                key={color?.brandColorId ?? `element-color-${i}`}
                                item={idToBrandColor[color?.brandColorId] || color}
                                onClick={onAppliedClick}
                                selected={isChecked(color)}
                            />
                        ))}
                    {openElementStylesModal && colorType === 'color' && (
                        <Dropdown.Item icon={<IconPlaylistAdd />} onClick={openElementStylesModal}>
                            {translate('editor.sidebar.shots.text.textStyle.configure-element-styles')}
                        </Dropdown.Item>
                    )}
                </>
            )}
            {searchInputValue === '' && configureColorsButton}
            {searchInputValue === '' && (configureColorsButton || !!elementColors.length) && <Divider />}
            {filterPalettes.map((palette, index) => (
                <Fragment key={palette.id}>
                    <Dropdown.SectionTitle>{palette.name}</Dropdown.SectionTitle>
                    {[...palette?.brandColors]
                        .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
                        .map((color) => (
                            <ColorItem
                                key={color?.id}
                                item={color}
                                onClick={setBrandColor}
                                selected={isChecked(color)}
                            />
                        ))}
                    {index < filterPalettes.length - 1 && <Dropdown.Divider />}
                </Fragment>
            ))}
        </>
    );
};

export default ColorSearch;
